import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsContext, type AnalyticsContextType } from '@/contexts/AnalyticsContext';

// Custom hook for analytics
export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

// Hook for automatic page view tracking
export function usePageTracking() {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const lastPathRef = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // Only track if path actually changed
    if (currentPath !== lastPathRef.current) {
      // Get page title from document or route
      const pageTitle = document.title || getPageTitleFromPath(location.pathname);

      trackPageView(currentPath, pageTitle, {
        hash: location.hash,
        state: location.state ? 'present' : 'null',
      });

      lastPathRef.current = currentPath;
    }
  }, [location, trackPageView]);
}

// Hook for tracking user session duration
export function useSessionTracking() {
  const { trackUserAction } = useAnalytics();
  const sessionStartRef = useRef<number>(Date.now());
  const isTrackingRef = useRef<boolean>(false);

  useEffect(() => {
    if (isTrackingRef.current) return;
    isTrackingRef.current = true;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const sessionDuration = Date.now() - sessionStartRef.current;
        trackUserAction('session_end', { session_duration: sessionDuration });
      } else if (document.visibilityState === 'visible') {
        sessionStartRef.current = Date.now();
        trackUserAction('session_start');
      }
    };

    const handleBeforeUnload = () => {
      const sessionDuration = Date.now() - sessionStartRef.current;
      trackUserAction('page_unload', { session_duration: sessionDuration });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Track initial session start
    trackUserAction('session_start');

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Track session end on unmount
      const sessionDuration = Date.now() - sessionStartRef.current;
      trackUserAction('session_end', { session_duration: sessionDuration });
    };
  }, [trackUserAction]);
}

// Hook for tracking form interactions
export function useFormTracking(formId: string, formType: string) {
  const { trackFormSubmission, trackUserAction } = useAnalytics();

  const trackFormStart = () => {
    trackUserAction('form_start', { form_id: formId, form_type: formType });
  };

  const trackFormSubmit = (additionalData?: Record<string, any>) => {
    trackFormSubmission(formId, formType, additionalData);
  };

  const trackFormError = (errorType: string, errorMessage: string) => {
    trackUserAction('form_error', {
      form_id: formId,
      form_type: formType,
      error_type: errorType,
      error_message: errorMessage
    });
  };

  const trackFormFieldFocus = (fieldName: string) => {
    trackUserAction('form_field_focus', {
      form_id: formId,
      form_type: formType,
      field_name: fieldName
    });
  };

  return {
    trackFormStart,
    trackFormSubmit,
    trackFormError,
    trackFormFieldFocus,
  };
}

// Hook for tracking button clicks with debouncing
export function useButtonTracking() {
  const { trackButtonClick } = useAnalytics();
  const debounceTimeoutRef = useRef<number | null>(null);

  const trackClick = (
    buttonId?: string,
    buttonText?: string,
    additionalData?: Record<string, any>,
    debounceMs: number = 300
  ) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = window.setTimeout(() => {
      trackButtonClick(buttonId, buttonText, additionalData);
    }, debounceMs);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return { trackClick };
}

// Helper function to get page title from path
function getPageTitleFromPath(pathname: string): string {
  const pathTitleMap: Record<string, string> = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/analyze': 'Property Analysis',
    '/portfolio': 'Portfolio',
    '/login': 'Login',
    '/logout': 'Logout',
    '/auth/callback': 'Authentication Callback',
  };

  return pathTitleMap[pathname] || 'Unknown Page';
}